// Shared skip-trace-agent flow, used by every surface that can hand owners
// to the recorder skip-trace agent (AOI roster, lead list, party record).
// One implementation so the hard-won behavior — load_close, verbatim
// submit, the 202 echo guard, session-blocked paste/refresh + auto
// re-submit, persistent stackable run panels — can never diverge across
// surfaces again.
//
// The caller builds the owners payload (surface-specific: each surface has
// different access to doc_numbers / trs / county) and calls submit(label,
// owners). Everything else — state, polling handoff to SkipTraceRunPanel,
// localStorage persistence keyed by scopeKey — lives here.

import { useRef, useState } from 'react';
import {
  agentApi, readRunId, readCount, readWeldSession, isSessionBlocked, readBlockReason,
  readValidated, readLoginState, readLoadCloseEcho,
  type WeldSession, type SkipTraceOwner,
} from '../services/agentApi';

export interface RunDescriptor { runId: string; label: string; startedAt: number; queued: number; }

const RUNS_KEY = (scope: string) => `mi:skiptrace-runs:${scope}`;
const MAX_RUNS = 12;

function loadRuns(scope: string): RunDescriptor[] {
  if (typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(RUNS_KEY(scope)) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((r: any) => r && typeof r.runId === 'string');
  } catch { return []; }
}
function saveRuns(scope: string, runs: RunDescriptor[]) {
  try { window.localStorage.setItem(RUNS_KEY(scope), JSON.stringify(runs)); } catch { /* quota/private */ }
}

export function useSkipTrace(scopeKey: string) {
  const [skipBusy, setSkipBusy] = useState(false);
  const [skipMsg, setSkipMsg] = useState<string | null>(null);
  // Load results into Close when the run finishes — defaults CHECKED.
  const [loadToClose, setLoadToClose] = useState(true);
  // Set when the box was checked but the agent's 202 echoed load_close:false.
  const [loadNotHonored, setLoadNotHonored] = useState<string | null>(null);
  // Persisted, stackable run panels (hydrated on mount → survive reload).
  const [runs, setRuns] = useState<RunDescriptor[]>(() => loadRuns(scopeKey));
  // Weld session state reported by the agent on submit.
  const [sessionBlocked, setSessionBlocked] = useState(false);
  const [weldSession, setWeldSession] = useState<WeldSession | null>(null);
  const [blockReason, setBlockReason] = useState<string | null>(null);
  // In-app session refresh (paste Cookie-Editor export → agent validates).
  const [sessionJsonDraft, setSessionJsonDraft] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState<{ ok: boolean; message: string } | null>(null);
  // Last submitted payload, so a session refresh (or Retry) re-submits it
  // exactly, regardless of what the surface's selection is now.
  const pendingRef = useRef<{ label: string; owners: SkipTraceOwner[] } | null>(null);

  function addRun(d: RunDescriptor) {
    setRuns(prev => {
      const next = [d, ...prev.filter(r => r.runId !== d.runId)].slice(0, MAX_RUNS);
      saveRuns(scopeKey, next);
      return next;
    });
  }
  function dismissRun(runId: string) {
    setRuns(prev => {
      const next = prev.filter(r => r.runId !== runId);
      saveRuns(scopeKey, next);
      return next;
    });
  }

  async function submit(label: string, owners: SkipTraceOwner[]) {
    if (owners.length === 0) return;
    pendingRef.current = { label, owners };
    setSkipBusy(true);
    setSkipMsg(null);
    setSessionBlocked(false);
    try {
      const resp = await agentApi.submitSkipTrace({ label, owners, load_close: loadToClose });
      setWeldSession(readWeldSession(resp));
      // Weld session gone → nothing queued. Show the refresh banner and stop.
      if (isSessionBlocked(resp)) {
        setSessionBlocked(true);
        setBlockReason(readBlockReason(resp));
        return;
      }
      const runId = readRunId(resp);
      const queued = readCount(resp, 'queued', 'queued_count', 'count') ?? owners.length;
      if (!runId) {
        setSkipMsg(`Submitted ${queued} owner${queued === 1 ? '' : 's'}, but the agent returned no run_id`);
        return;
      }
      // Loud guard: the agent echoes the resolved load_close. If we asked
      // for it but got false, surface it — the silent failure that wasted
      // batches (dropped key / stale bundle / server override).
      const echo = readLoadCloseEcho(resp);
      setLoadNotHonored(
        loadToClose && echo.value === false
          ? `Load-to-Close was NOT honored — the agent's response says load_close: false${
              echo.sourceKey ? ` (it read key "${echo.sourceKey}")` : ' (no load_close key was received — likely a stale browser bundle; hard-reload)'}.`
          : null
      );
      addRun({ runId, label, startedAt: Date.now(), queued });
    } catch (e: any) {
      setSkipMsg(`Skip trace failed: ${e?.message ?? 'unknown'}`);
    } finally {
      setSkipBusy(false);
    }
  }

  // Re-submit the exact last payload (session-refresh auto-resubmit + the
  // banner's "Retry without refresh").
  function retryPending(): Promise<void> {
    const p = pendingRef.current;
    return p ? submit(p.label, p.owners) : Promise.resolve();
  }

  async function refreshSession() {
    const paste = sessionJsonDraft.trim();
    if (!paste) { setRefreshResult({ ok: false, message: 'Paste the exported session JSON first.' }); return; }
    setRefreshing(true);
    setRefreshResult(null);
    try {
      const resp = await agentApi.refreshWeldSession(paste);
      const ws = readWeldSession(resp);
      if (ws) setWeldSession(ws);
      const loginState = readLoginState(resp);
      if (readValidated(resp)) {
        setRefreshResult({ ok: true, message: `Session validated${loginState ? ` · ${loginState}` : ''} — re-submitting…` });
        setSessionBlocked(false);
        setSessionJsonDraft('');
        await retryPending();
      } else {
        const detail = readBlockReason(resp);
        setRefreshResult({ ok: false, message: `Not validated${loginState ? ` · ${loginState}` : ''}${detail ? ` · ${detail}` : ''}` });
      }
    } catch (e: any) {
      setRefreshResult({ ok: false, message: e?.message ?? 'Session refresh failed' });
    } finally {
      setRefreshing(false);
    }
  }

  return {
    skipBusy, skipMsg, setSkipMsg,
    loadToClose, setLoadToClose,
    loadNotHonored, setLoadNotHonored,
    runs, dismissRun,
    sessionBlocked, setSessionBlocked, weldSession, blockReason,
    sessionJsonDraft, setSessionJsonDraft, refreshing, refreshResult, refreshSession,
    submit, retryPending,
    hasPending: pendingRef.current != null,
  };
}

export type SkipTraceController = ReturnType<typeof useSkipTrace>;
